"use client";

import React, { useState, type FormEvent } from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Loader as Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType] = useState("Home Addition");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    setZipError("");
    setConsentError("");
    let hasError = false;

    // Validate 5-digit ZIP code
    if (!/^\d{5}$/.test(zip.trim())) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      hasError = true;
    }

    // Validate consent
    if (!consent) {
      setConsentError("Please agree to receive communications regarding your project.");
      hasError = true;
    }
    if (hasError) {
      return;
    }
    setIsSubmitting(true);
    try {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const bodyParams = new URLSearchParams({
        name,
        phone,
        email,
        project_type: projectType,
        address: zip,
        zip,
        details: message,
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: pageUrl,
        form_location: "home-additions-2-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: bodyParams
      });
      if (response && response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="rounded-2xl border border-border bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10 md:p-12">
          <div className="max-w-2xl mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold tracking-tight text-foreground">
              Request an estimate
            </h2>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              Share details about your planned home addition. We review scope, site feasibility, and schedule an initial consultation.
            </p>
          </div>

          {isSuccess ? <div className="rounded-xl border border-border bg-card p-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/20 text-primary mb-2">
                <CheckCircle2 className="size-6 text-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">
                Estimate request received
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Thank you for contacting Brookhaven Elevation Renovations. Our team will review your project details and follow up shortly.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-6">
              {errorMessage && <Alert variant="destructive" className="border-destructive/30">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Unable to submit request</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="full-name" className="text-foreground">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="full-name" type="text" required placeholder="Jane Doe" value={name} onChange={e => setName(e.target.value)} className="bg-card" />
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <Label htmlFor="phone-number" className="text-foreground">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="phone-number" type="tel" required placeholder="(404) 555-0192" value={phone} onChange={e => setPhone(e.target.value)} className="bg-card" />
                </div>

                {/* Email Address */}
                <div className="space-y-2">
                  <Label htmlFor="email-address" className="text-foreground">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="email-address" type="email" required placeholder="jane@example.com" value={email} onChange={e => setEmail(e.target.value)} className="bg-card" />
                </div>

                {/* ZIP Code */}
                <div className="space-y-2">
                  <Label htmlFor="zip-code" className="text-foreground">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="zip-code" type="text" required placeholder="30319" value={zip} maxLength={5} onChange={e => {
                setZip(e.target.value);
                if (zipError) setZipError("");
              }} aria-invalid={Boolean(zipError)} className="bg-card" />
                  {zipError && <p className="text-xs text-destructive mt-1">{zipError}</p>}
                </div>

                {/* Project Type */}
                <div className="space-y-2">
                  <Label htmlFor="project-type" className="text-foreground">
                    Project Type <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <select id="project-type" required disabled value={projectType} className="border-input flex h-9 w-full rounded-md border bg-muted/50 px-3 py-1 text-base shadow-xs md:text-sm text-foreground opacity-90 cursor-not-allowed outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]">
                      {PROJECT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>
                          {opt}
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Preferred Timing */}
                <div className="space-y-2">
                  <Label htmlFor="timing" className="text-foreground">
                    Preferred Timing
                  </Label>
                  <select id="timing" value={timing} onChange={e => setTiming(e.target.value)} className="border-input file:text-foreground placeholder:text-muted-foreground flex h-9 w-full rounded-md border bg-card px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm text-foreground">
                    <option value="">Select a timeline</option>
                    {TIMING_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>
              </div>

              {/* Budget Range */}
              <div className="space-y-2">
                <Label htmlFor="budget" className="text-foreground">
                  Budget Range
                </Label>
                <select id="budget" value={budget} onChange={e => setBudget(e.target.value)} className="border-input file:text-foreground placeholder:text-muted-foreground flex h-9 w-full rounded-md border bg-card px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm text-foreground">
                  <option value="">Select an estimated investment range</option>
                  {BUDGET_OPTIONS.map(opt => <option key={opt} value={opt}>
                      {opt}
                    </option>)}
                </select>
              </div>

              {/* Project Details / Message */}
              <div className="space-y-2">
                <Label htmlFor="project-details" className="text-foreground">
                  Project Details / Message
                </Label>
                <Textarea id="project-details" rows={4} placeholder="Tell us about the addition you have in mind (e.g., ground floor expansion, second story, primary suite addition)..." value={message} onChange={e => setMessage(e.target.value)} className="bg-card" />
              </div>

              {/* Consent Checkbox */}
              <div className="space-y-2 pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox id="consent-checkbox" checked={consent} onCheckedChange={checked => {
                setConsent(Boolean(checked));
                if (consentError) setConsentError("");
              }} className="mt-0.5" />
                  <Label htmlFor="consent-checkbox" className="text-sm font-normal leading-relaxed text-foreground cursor-pointer select-none">
                    I agree to be contacted about my project by phone, text, or email.
                  </Label>
                </div>
                {consentError && <p className="text-xs text-destructive">{consentError}</p>}
              </div>

              {/* Submit CTA */}
              <div className="pt-4">
                <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground font-semibold px-7 py-3.5 rounded-lg h-auto shadow-sm hover:bg-primary/90 transition-all text-base w-full sm:w-auto">
                  {isSubmitting ? <>
                      <Loader2 className="size-4 animate-spin" />
                      Submitting request...
                    </> : "Submit estimate request"}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}